import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MapPin,
  Navigation,
  RefreshCw,
  Route,
  Truck,
  UserCheck,
} from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { apiFetch } from "@/lib/api-client";
import { formatDateUtc, formatNumber } from "@/lib/i18n";
import { Skeleton } from "@/components/skeleton";

type DispatchStatus = "pending" | "assigned" | "in_progress" | "completed" | "cancelled";
type DispatchActionStatus = "en_route" | "arrived" | "in_progress" | "completed";

interface DispatchJob {
  id: string;
  title: string;
  description: string | null;
  customerName: string;
  status: DispatchStatus;
  canonicalStatus: string;
  scheduledDate: string;
  scheduledTime: string | null;
  assignedStaffId: string | null;
  assignedStaffName: string | null;
  requiredSkills: string[];
  notificationGap: string;
}

interface DispatchSuggestion {
  staffId: string;
  staffName: string;
  score: number;
  distanceKm: number | null;
  hasAllSkills: boolean;
  factors: {
    proximity: number;
    skillMatch: number;
    workloadBalance: number;
  };
  proximityMode: "job_location_fallback" | "global_fallback";
  activeJobCount: number;
  maxDailyJobs: number;
}

interface DispatchBoard {
  items: DispatchJob[];
  count: number;
  filters: {
    status: DispatchStatus | null;
    limit: number;
  };
}

interface SuggestionResponse {
  data: {
    job: DispatchJob;
    suggestions: DispatchSuggestion[];
  };
}

interface AssignmentResponse {
  data: {
    job: DispatchJob;
    assignment: {
      staffId: string;
      staffName: string;
      linkedBookingCount: number;
      eventType: string;
      notificationAttempted: boolean;
      notificationGap: string;
    };
  };
}

interface StatusResponse {
  data: {
    job: DispatchJob;
    transition: {
      requestedStatus: DispatchActionStatus;
      persistedStatus: string;
      changed: boolean;
      eventType: string | null;
      notificationAttempted: boolean;
      notificationGap: string;
    };
  };
}

const STATUS_LABELS: Record<DispatchStatus, string> = {
  pending: "Pending",
  assigned: "Assigned",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_ACTIONS: Array<{ status: DispatchActionStatus; label: string }> = [
  { status: "en_route", label: "Mark en route" },
  { status: "arrived", label: "Mark arrived" },
  { status: "in_progress", label: "Start job" },
  { status: "completed", label: "Complete job" },
];

function scheduleLabel(job: DispatchJob): string {
  const date = formatDateUtc(job.scheduledDate);
  return job.scheduledTime ? `${date} at ${job.scheduledTime}` : date;
}

function scoreTone(score: number): string {
  if (score >= 80) return "text-[var(--color-success)]";
  if (score >= 60) return "text-[var(--color-warning)]";
  return "text-[var(--color-muted-foreground)]";
}

export default function FieldDispatchPage() {
  const { data, isLoading, error, refetch } = useApi<DispatchBoard>("/crm/dispatch/jobs");
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJobOverride, setSelectedJobOverride] = useState<DispatchJob | null>(null);
  const [suggestions, setSuggestions] = useState<DispatchSuggestion[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  const jobs = data?.items ?? [];
  const selectedJob = selectedJobOverride?.id === selectedJobId
    ? selectedJobOverride
    : jobs.find((job) => job.id === selectedJobId) ?? jobs[0] ?? null;

  useEffect(() => {
    if (jobs.length === 0) {
      setSelectedJobId(null);
      return;
    }
    if (!selectedJobId || !jobs.some((job) => job.id === selectedJobId)) {
      setSelectedJobId(jobs[0]!.id);
    }
  }, [jobs, selectedJobId]);

  async function loadSuggestions(job: DispatchJob) {
    setLoadingAction(`suggest-${job.id}`);
    setActionError(null);
    setMessage(null);
    try {
      const response = await apiFetch<SuggestionResponse>(`/crm/dispatch/jobs/${job.id}/suggestions`);
      setSelectedJobOverride(response.data.job);
      setSuggestions(response.data.suggestions);
      setMessage(response.data.suggestions.length > 0
        ? `Loaded ${response.data.suggestions.length} assignment suggestions.`
        : "No available staff suggestions for this job.");
    } catch (err) {
      setSuggestions([]);
      setActionError(err instanceof Error ? err.message : "Could not load dispatch suggestions.");
    } finally {
      setLoadingAction(null);
    }
  }

  async function assignStaff(job: DispatchJob, suggestion: DispatchSuggestion) {
    setLoadingAction(`assign-${suggestion.staffId}`);
    setActionError(null);
    setMessage(null);
    try {
      const response = await apiFetch<AssignmentResponse>(`/crm/dispatch/jobs/${job.id}/assign`, {
        method: "POST",
        body: JSON.stringify({ staffId: suggestion.staffId }),
      });
      setSelectedJobOverride(response.data.job);
      setMessage(`Assigned ${response.data.assignment.staffName}. ${response.data.assignment.notificationGap}`);
      setSuggestions([]);
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not assign field staff.");
    } finally {
      setLoadingAction(null);
    }
  }

  async function updateStatus(job: DispatchJob, status: DispatchActionStatus) {
    setLoadingAction(`status-${status}`);
    setActionError(null);
    setMessage(null);
    try {
      const response = await apiFetch<StatusResponse>(`/crm/dispatch/jobs/${job.id}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      });
      setSelectedJobOverride(response.data.job);
      setMessage(`${response.data.transition.requestedStatus.replace("_", " ")} saved. ${response.data.transition.notificationGap}`);
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not update dispatch status.");
    } finally {
      setLoadingAction(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-primary)]">Service delivery</p>
          <h1 className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">Field Dispatch</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Mobile job assignment, route-aware staff suggestions, and operational status tracking from the live CRM dispatch runtime.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh board
        </button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.35fr)]">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[var(--color-error)]/30 bg-[var(--color-error)]/10 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-[var(--color-error)]" />
            <div>
              <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Dispatch board is unavailable</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{error}</p>
            </div>
          </div>
        </div>
      ) : jobs.length === 0 ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="flex items-start gap-3">
            <Truck className="mt-0.5 h-5 w-5 text-[var(--color-muted-foreground)]" />
            <div>
              <h2 className="text-sm font-semibold text-[var(--color-foreground)]">No dispatch jobs found</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Jobs appear here after the CRM has confirmed service jobs with linked booking or property context.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.35fr)]">
          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Dispatch queue</h2>
                <p className="text-xs text-[var(--color-muted-foreground)]">{formatNumber(data?.count ?? jobs.length)} visible jobs</p>
              </div>
              <Truck className="h-5 w-5 text-[var(--color-primary)]" />
            </div>
            <div className="space-y-2">
              {jobs.map((job) => (
                <button
                  key={job.id}
                  type="button"
                  onClick={() => {
                    setSelectedJobId(job.id);
                    setSelectedJobOverride(null);
                    setSuggestions([]);
                    setMessage(null);
                    setActionError(null);
                  }}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    selectedJob?.id === job.id
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                      : "border-[var(--color-border)] hover:bg-[var(--color-surface)]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-[var(--color-foreground)]">{job.title}</div>
                      <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{job.customerName} · {scheduleLabel(job)}</div>
                    </div>
                    <span className="rounded-full bg-[var(--color-surface)] px-2 py-1 text-[11px] font-medium text-[var(--color-muted-foreground)]">
                      {STATUS_LABELS[job.status]}
                    </span>
                  </div>
                  {job.assignedStaffName ? (
                    <div className="mt-2 text-xs text-[var(--color-success)]">Assigned to {job.assignedStaffName}</div>
                  ) : null}
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            {selectedJob ? (
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{selectedJob.title}</h2>
                    <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{selectedJob.description ?? "No job description provided."}</p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-[var(--color-muted-foreground)]">
                      <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-surface)] px-2 py-1">
                        <MapPin className="h-3 w-3" />
                        {selectedJob.customerName}
                      </span>
                      <span className="rounded-full bg-[var(--color-surface)] px-2 py-1">{scheduleLabel(selectedJob)}</span>
                      <span className="rounded-full bg-[var(--color-surface)] px-2 py-1">{STATUS_LABELS[selectedJob.status]}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadSuggestions(selectedJob)}
                    disabled={loadingAction !== null}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
                  >
                    {loadingAction === `suggest-${selectedJob.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Route className="h-4 w-4" />}
                    Optimize route
                  </button>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-[var(--color-border)] p-3">
                    <div className="text-xs font-medium text-[var(--color-muted-foreground)]">Assigned field staff</div>
                    <div className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{selectedJob.assignedStaffName ?? "Unassigned"}</div>
                  </div>
                  <div className="rounded-lg border border-[var(--color-border)] p-3">
                    <div className="text-xs font-medium text-[var(--color-muted-foreground)]">Required skills</div>
                    <div className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">
                      {selectedJob.requiredSkills.length > 0 ? selectedJob.requiredSkills.join(", ") : "No specific skills"}
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Status tracking</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {STATUS_ACTIONS.map((action) => (
                      <button
                        key={action.status}
                        type="button"
                        onClick={() => updateStatus(selectedJob, action.status)}
                        disabled={loadingAction !== null}
                        className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-60"
                      >
                        {loadingAction === `status-${action.status}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>

                {message ? (
                  <div className="mt-4 rounded-lg border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 p-3 text-sm text-[var(--color-foreground)]">
                    {message}
                  </div>
                ) : null}
                {actionError ? (
                  <div className="mt-4 rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error)]/10 p-3 text-sm text-[var(--color-foreground)]">
                    {actionError}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <div className="mb-3 flex items-center gap-2">
                <Navigation className="h-4 w-4 text-[var(--color-primary)]" />
                <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Route-aware assignment suggestions</h2>
              </div>
              {suggestions.length === 0 ? (
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Select a job and run Optimize route to rank available field staff by proximity, required skills, and workload balance.
                </p>
              ) : (
                <div className="space-y-3">
                  {suggestions.map((suggestion) => (
                    <div key={suggestion.staffId} className="rounded-lg border border-[var(--color-border)] p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <UserCheck className="h-4 w-4 text-[var(--color-primary)]" />
                            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{suggestion.staffName}</h3>
                            <span className={`text-xs font-semibold ${scoreTone(suggestion.score)}`}>{suggestion.score}% match</span>
                          </div>
                          <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                            {suggestion.distanceKm === null ? "No job coordinates; using global workload fallback." : `${formatNumber(suggestion.distanceKm)} km from job`}
                            {" · "}
                            {suggestion.activeJobCount}/{suggestion.maxDailyJobs} active jobs
                            {" · "}
                            {suggestion.hasAllSkills ? "all skills covered" : "skill gap"}
                          </p>
                          <div className="mt-3 grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-3">
                            <span>Proximity {suggestion.factors.proximity}%</span>
                            <span>Skills {suggestion.factors.skillMatch}%</span>
                            <span>Workload {suggestion.factors.workloadBalance}%</span>
                          </div>
                        </div>
                        {selectedJob ? (
                          <button
                            type="button"
                            onClick={() => assignStaff(selectedJob, suggestion)}
                            disabled={loadingAction !== null}
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] px-3 py-2 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60"
                          >
                            {loadingAction === `assign-${suggestion.staffId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
                            Assign
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
